/**
 * Main orchestrating hook for EntityForm.
 *
 * Composes all five internal hooks into the single public API that powers
 * the entire form lifecycle: data fetching, value management, conditional
 * field evaluation, validation, and submission.
 *
 * This is the only hook that consumers import directly. It returns the full
 * `UseEntityFormReturn` interface, enabling both the compound component
 * tree and headless usage patterns.
 *
 * @see Spec section 4.1 for the return type definition.
 * @see Plan section 3.6 for the composition strategy.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

import type {
  EntityFormConfig,
  FieldConfig,
  FieldType,
  UseEntityFormReturn,
} from '../types';

import { useFetchEntity } from './useFetchEntity';
import { useFormValues } from './useFormValues';
import { useFieldConditions } from './useFieldConditions';
import { useFormValidation } from './useFormValidation';
import { useFormSubmission } from './useFormSubmission';
import { handleCancel } from '../workflow/handlers';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Type-appropriate empty value for an input whose store entry is
 * undefined. Keeps inputs controlled from mount without mutating the
 * underlying form values store.
 */
function defaultControlledValueFor(
  type: FieldType | undefined,
): unknown {
  switch (type) {
    case 'checkbox':
      return false;
    case 'checkboxGroup':
      return [];
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

/**
 * Options accepted by the `useEntityForm` hook.
 *
 * At minimum, a `config` must be provided. All other options are optional
 * and control the form's operating mode and lifecycle callbacks.
 */
export interface UseEntityFormOptions {
  /** The full EntityForm configuration. */
  config: EntityFormConfig;

  /**
   * Initial values for the form fields.
   * Takes precedence over field-level `defaultValue` settings.
   */
  initialValues?: Record<string, unknown>;

  /**
   * Entity ID for edit mode.
   * When provided alongside a `fetchEndpoint`, triggers data fetching.
   */
  entityId?: string | number;

  /**
   * Form mode. Defaults to `'edit'` when `entityId` or `initialValues`
   * is provided, otherwise `'create'`.
   */
  mode?: 'create' | 'edit';

  /** Called when the form is successfully submitted. */
  onSuccess?: (response: unknown) => void;

  /** Called when the form submission fails. */
  onError?: (error: unknown) => void;

  /** Called when the cancel action is triggered. */
  onCancel?: () => void;

  /**
   * Custom mutation function that performs the API call.
   * When provided, bypasses the default fetch-based submission.
   * Receives the built payload and config, must return a promise.
   */
  mutationFn?: (
    payload: Record<string, unknown> | FormData,
    config: EntityFormConfig,
  ) => Promise<unknown>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Main public hook that orchestrates the EntityForm lifecycle.
 *
 * Composes the five internal hooks in order:
 *
 * 1. **useFetchEntity** -- fetches entity data for edit mode.
 * 2. **useFormValues**  -- Zustand-backed value management and dirty tracking.
 * 3. **useFieldConditions** -- evaluates conditional visibility/required/disabled.
 * 4. **useFormValidation**  -- Zod schema validation and error tracking.
 * 5. **useFormSubmission**  -- mutation lifecycle, payload building, and error handling.
 *
 * The return value satisfies the full `UseEntityFormReturn` interface,
 * enabling both compound component rendering and headless form control.
 *
 * @param options - Hook options including config, initialValues, entityId, etc.
 * @returns The complete form state and action API.
 *
 * @example
 * ```tsx
 * const form = useEntityForm({
 *   config: userFormConfig,
 *   entityId: userId,
 *   onSuccess: () => navigate('/users'),
 * });
 *
 * // Compound usage:
 * <EntityForm config={config} form={form}>
 *   <EntityForm.Fields />
 *   <EntityForm.Actions />
 * </EntityForm>
 *
 * // Headless usage:
 * <input {...form.getFieldProps('email')} />
 * <button onClick={form.submit}>Save</button>
 * ```
 */
export function useEntityForm(options: UseEntityFormOptions): UseEntityFormReturn {
  const {
    config,
    initialValues: propsInitialValues,
    entityId,
    onSuccess,
    onError,
    onCancel,
    mutationFn,
  } = options;

  // ---- 1. Fetch entity data (edit mode) -----------------------------------

  const fetch = useFetchEntity(
    config.endpoint.fetchEndpoint,
    entityId,
    config.endpoint.responseMapping,
  );

  // ---- 2. Form values (Zustand store) -------------------------------------

  const formValues = useFormValues(config.fields, propsInitialValues);

  // ---- Merge fetched data into form values --------------------------------

  /**
   * Track whether we've already merged the fetched data to avoid
   * re-applying on every render. We use a ref keyed on the fetch data
   * reference so that a refetch triggers a fresh merge.
   */
  const lastMergedDataRef = useRef<Record<string, unknown> | undefined>(undefined);

  useEffect(() => {
    if (fetch.data && fetch.data !== lastMergedDataRef.current) {
      lastMergedDataRef.current = fetch.data;
      formValues.setValues(fetch.data);
    }
  }, [fetch.data, formValues]);

  // ---- 3. Conditional field evaluation ------------------------------------

  // `loadGeneration` lets the rule overlay's edge-triggered `checked` effect
  // tell an edit apart from a bulk load (fetch merge / reset), so it never
  // overwrites values that were loaded rather than typed.
  const conditions = useFieldConditions(
    config.fields,
    formValues.values,
    formValues.setValue,
    formValues.loadGeneration,
  );

  // ---- 4. Validation ------------------------------------------------------

  // The required map carries condition / rule-overlay `required` effects, so
  // a field made required at runtime is validated (not just asterisked).
  // `config.validation` carries the form-level cross-field rules; they are
  // keyed on their content, so a config rebuilt on every render is fine.
  const validation = useFormValidation(
    config.fields,
    formValues.values,
    conditions.visibilityMap,
    config.validation,
    conditions.requiredMap,
  );

  // ---- 5. Submission lifecycle --------------------------------------------

  const submission = useFormSubmission({
    config,
    values: formValues.values,
    visibilityMap: conditions.visibilityMap,
    validateForm: validation.validateForm,
    errors: validation.errors,
    setError: validation.setError,
    onSuccess,
    onError,
    mutationFn,
  });

  // ---- Helper: getError ---------------------------------------------------

  /**
   * Returns the error message for a specific field, or `undefined`
   * if the field has no error.
   */
  const getError = useCallback(
    (fieldName: string): string | undefined => {
      return validation.errors[fieldName];
    },
    [validation.errors],
  );

  // ---- Helper: isTouched --------------------------------------------------

  /**
   * Returns whether a specific field has been touched (blurred at least once).
   */
  const isTouched = useCallback(
    (fieldName: string): boolean => {
      return validation.touched[fieldName] === true;
    },
    [validation.touched],
  );

  // ---- Helper: getFieldProps ----------------------------------------------

  /**
   * Returns a props object for a specific field that can be spread onto
   * a base input component.
   *
   * Combines value/onChange from useFormValues, error state from
   * useFormValidation, required/disabled from useFieldConditions,
   * and generates standard `name` and `id` attributes.
   */
  const getFieldProps = useCallback(
    (fieldName: string) => {
      const fieldConfig = config.fields[fieldName] as FieldConfig | undefined;

      const rawValue = formValues.values[fieldName];
      const value =
        rawValue === undefined
          ? defaultControlledValueFor(fieldConfig?.type)
          : rawValue;

      return {
        value,
        onChange: (value: unknown) => {
          formValues.setValue(fieldName, value);
        },
        onBlur: () => {
          validation.setTouched(fieldName);
        },
        error: validation.errors[fieldName] !== undefined,
        errorMessage: validation.errors[fieldName],
        disabled: conditions.isFieldDisabled(fieldName) || (fieldConfig?.disabled === true),
        required: conditions.isFieldRequired(fieldName),
        name: fieldName,
        id: `field-${fieldName}`,
      };
    },
    [config.fields, formValues, validation, conditions],
  );

  // ---- Action: reset ------------------------------------------------------

  /**
   * Resets the form to its initial values (or to new values if provided).
   * Also clears all validation errors.
   */
  const reset = useCallback(
    (newInitialValues?: Record<string, unknown>) => {
      formValues.reset(newInitialValues);
      validation.clearErrors();
      submission.setFormError(null);
    },
    [formValues, validation, submission],
  );

  // ---- Action: cancel -----------------------------------------------------

  /**
   * Processes the cancel workflow.
   *
   * Delegates to `handleCancel` from the workflow handlers to determine
   * whether a confirmation dialog should be shown and/or a redirect
   * should occur, then calls the `onCancel` callback if provided.
   */
  const cancel = useCallback(() => {
    handleCancel(formValues.isDirty, config);
    onCancel?.();
  }, [formValues.isDirty, config, onCancel]);

  // ---- Compose return value -----------------------------------------------

  /**
   * Memoise the public API object so that `form` keeps a stable identity
   * across renders and only produces a new reference when an underlying
   * piece of state actually changes.
   *
   * This matters because consumers routinely place the returned `form`
   * object in `useEffect` dependency arrays (and `EntityFormRoot` feeds it
   * into the context `value`). A fresh object literal on every render would
   * re-run those effects unconditionally — a footgun that previously armed
   * an infinite gestion2 fetch loop in MiseAJourDossierCard.
   *
   * Each sub-hook now returns a referentially-stable object (its return is
   * itself memoised), and the local helpers below are `useCallback`-stable,
   * so this memo recomputes precisely when — and only when — the form's
   * observable state changes.
   */
  return useMemo<UseEntityFormReturn>(
    () => ({
      // Form Values
      values: formValues.values,
      initialValues: formValues.initialValues,

      // Field Helpers
      getValue: formValues.getValue,
      setValue: formValues.setValue,
      setValues: formValues.setValues,
      getError,
      setError: validation.setError,
      clearError: validation.clearError,
      isTouched,
      setTouched: validation.setTouched,
      isFieldDirty: formValues.isFieldDirty,
      getFieldProps,

      // Validation State
      errors: validation.errors,
      touched: validation.touched,
      validateField: validation.validateField,
      validateForm: validation.validateForm,

      // Form State
      isDirty: formValues.isDirty,
      isValid: validation.isValid,
      isSubmitting: submission.isSubmitting,
      isSubmitSuccessful: submission.isSubmitSuccessful,
      submitCount: submission.submitCount,
      formError: submission.formError,

      // Form Actions
      submit: submission.submit,
      reset,
      cancel,
      clearErrors: validation.clearErrors,

      // Conditional Field State
      isFieldVisible: conditions.isFieldVisible,
      isFieldRequired: conditions.isFieldRequired,

      // Loading State
      isLoading: fetch.isLoading,
      fetchError: fetch.error,

      // Config Access
      config,
      resolvedFields: conditions.resolvedFields,
    }),
    [
      formValues,
      validation,
      conditions,
      submission,
      fetch,
      getError,
      isTouched,
      getFieldProps,
      reset,
      cancel,
      config,
    ],
  );
}
